import { useTheme } from "expo-router/react-navigation";
import { Check, Copy } from "lucide-react-native";
import { ComponentRef, FC, useMemo, useRef, useState } from "react";
import { View } from "react-native";

import { BarChart, GroupedBarChart } from "@/components/charts";
import { Box, Card, Pressable, Text, VStack } from "@/components/ui";
import { useLocalization } from "@/hooks";
import { QuestionResults } from "@/types/resources";
import { copyChartToClipboard } from "./copyChartToClipboard";

/* ── Types ─────────────────────────────────────────────── */

interface SingleChoiceQuestionResultsProps {
  compact?: boolean;
  result: QuestionResults;
  selectedGroupIndex: "all" | string;
}

/* ── Component ──────────────────────────────────────────── */

const SingleChoiceQuestionResults: FC<SingleChoiceQuestionResultsProps> = ({
  compact = false,
  result,
  selectedGroupIndex,
}) => {
  const { i18n } = useLocalization();
  const theme = useTheme();
  const { groups, question } = result;

  const isSingleGroup = groups.length === 1;
  const chartRef = useRef<ComponentRef<typeof View>>(null);
  const [copied, setCopied] = useState(false);

  const options =
    question.options.length > 0
      ? question.options
      : question.media.map((_: string, idx: number) =>
          i18n.t("questions.option", { number: idx + 1 })
        );

  const totalRespondents = useMemo(
    () =>
      groups.reduce(
        (sum, g) =>
          sum +
          g.answers.filter(
            ({ answer }) =>
              answer.type === "single_choice" && answer.choice !== null
          ).length,
        0
      ),
    [groups]
  );

  const title = i18n.t("dashboardNavigator.chartTitleResponseDistribution", {
    count: totalRespondents,
  });

  // Cap the chart at its natural width (mirrors BarChart's own per-option
  // sizing: ~60px/option + padding, floored at its 400px minWidth) and center
  // it, so a couple of bars don't stretch edge-to-edge when the results column
  // is full-width (use-case studies have no sidebar).
  const chartMaxWidth = Math.max(options.length * 60 + 120, 400);

  const barChartPoints = useMemo(() => {
    if (!isSingleGroup) return [];
    const counts: number[] = options.map(() => 0);
    groups[0].answers
      .filter(
        ({ answer }) =>
          answer.type === "single_choice" && answer.choice !== null
      )
      .forEach(({ answer }) => {
        if (answer.type === "single_choice") counts[answer.choice as number]++;
      });
    return options.map((opt, idx) => ({
      x: `${idx + 1}. ${opt}`,
      y: counts[idx],
    }));
  }, [groups, isSingleGroup, options]);

  const barChartGroups = useMemo(() => {
    if (isSingleGroup) return [];
    return groups.map((group, groupIdx) => {
      const counts: number[] = options.map(() => 0);
      group.answers
        .filter(
          ({ answer }) =>
            answer.type === "single_choice" && answer.choice !== null
        )
        .forEach(({ answer }) => {
          if (answer.type === "single_choice")
            counts[answer.choice as number]++;
        });
      return {
        key: group.group.id || `group-${groupIdx}`,
        label:
          group.group.name ||
          group.group.panel?.name ||
          (i18n.t("studies.group", { index: groupIdx + 1 }) as string),
        points: options.map((opt, idx) => ({
          x: `${idx + 1}. ${opt}`,
          y: counts[idx],
        })),
        respondentCount: group.answers.filter(
          ({ answer }) =>
            answer.type === "single_choice" && answer.choice !== null
        ).length,
      };
    });
  }, [groups, i18n, isSingleGroup, options]);

  const copyVisual = async () => {
    const element = chartRef.current as unknown as HTMLElement;
    if (!element) return;
    await copyChartToClipboard(element, () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Card
      className={`group rounded-2xl ${compact ? "border-0 p-0 bg-transparent shadow-none" : "border border-outline-200 px-6 pt-6 pb-2"}`}
    >
      <Box
        className={`absolute z-10 opacity-0 group-hover:opacity-100 transition-opacity ${compact ? "top-0 right-0" : "top-4 right-4"}`}
      >
        <Pressable onPress={copyVisual}>
          <Box className="p-2 rounded-lg bg-background-100 dark:bg-background-800 hover:bg-background-200 dark:hover:bg-background-700 transition-colors">
            {copied ? (
              <Check color="rgb(var(--color-success-500))" size={16} />
            ) : (
              <Copy color={theme.colors.text} size={16} />
            )}
          </Box>
        </Pressable>
      </Box>
      <VStack className="gap-6">
        {title && (
          <Text className="text-sm font-semibold text-typography-900 dark:text-typography-100 pr-8">
            {title}
          </Text>
        )}
        <View
          ref={chartRef}
          style={{
            alignItems: "flex-start",
            justifyContent: "center",
            width: "100%",
          }}
        >
          {isSingleGroup ? (
            <Box style={{ maxWidth: chartMaxWidth, width: "100%" }}>
              <BarChart
                points={barChartPoints}
                totalRespondents={totalRespondents}
              />
            </Box>
          ) : (
            <GroupedBarChart
              groups={barChartGroups}
              minWidth={selectedGroupIndex === "all" ? 600 : 400}
            />
          )}
        </View>
      </VStack>
    </Card>
  );
};

/* ── Export ─────────────────────────────────────────────── */

export default SingleChoiceQuestionResults;
